import { ChangeDetectorRef, Component, OnInit, Renderer2, OnDestroy, ApplicationRef } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LoaderService } from '@shared/components/loader/loader.service';
import { concat, interval, Subject } from 'rxjs';
import { ThemingService } from '@shared/services/theming.service';
import { DataStoreService } from '@shared/services/dataStore.service';
import { GoToTopComponent } from '@shared/components/go-to-top/go-to-top.component';
import {
  ActivatedRoute,
  ActivationStart,
  NavigationCancel,
  NavigationEnd,
  NavigationError,
  NavigationExtras,
  NavigationStart,
  Router,
  RoutesRecognized
} from '@angular/router';
import { PopupService } from '@shared/modules/popup/services/popup.service';
import { RoutingInfoService } from '@shared/services/routingInfo.service';
import { filter, first, pairwise } from 'rxjs/operators';
import { AuthService } from '@shared/services/auth.service';
import { ToastService } from '@shared/modules/toast/services/toast.service';
import {
  NgcCookieConsentService,
  NgcInitializeEvent,
  NgcStatusChangeEvent,
  NgcNoCookieLawEvent
} from 'ngx-cookieconsent';
import { Subscription } from 'rxjs';
import { SwUpdate } from '@angular/service-worker';
import { environment } from '@environment';
import { RunTimeConfigService } from '@shared/services/run-time-config.service';
import { OAuthErrorEvent, OAuthService } from 'angular-oauth2-oidc';
import { FeatureService } from '@shared/services/feature.service';
import { CommunicationService } from '@shared/services/communication.service';
import { LangService } from '@shared/components/lang-bar/lang.service';
import { NonceService } from '@shared/services/nonce-generator';
import cspConfig from 'src/assets/configs/EAF/csp-header-config.json';
declare global {
  interface Window {
    Chatroom: any;
  }
}

@Component({
  selector: 'app-root',
  templateUrl: './root.component.html',
  styleUrls: ['./root.component.scss']
})
export class RootComponent implements OnInit, OnDestroy {
  title = 'CIAP-App';
  defaultLanguage = 'en';
  isLoading: Subject<boolean> = this.loaderService.isLoading;
  isLoaderShowing: boolean;
  sessionExpiryTime = 3600; // in seconds

  // hasToasts: Subject<boolean> = this.popupService.hasToasts;
  noWrapperClass: boolean;
  currentTheme: string;
  isIE: boolean;
  isAuthenticated: boolean;
  isbotVisible = false;
  appUpdateAvailable: boolean;
  readonly authConfig: any;
  nonce: string = '';
  // keep refs to subscriptions to be able to unsubscribe later
  private popupOpenSubscription: Subscription;
  private popupCloseSubscription: Subscription;
  private initializeSubscription: Subscription;
  private statusChangeSubscription: Subscription;
  private revokeChoiceSubscription: Subscription;
  private noCookieLawSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private loaderService: LoaderService,
    private themingService: ThemingService,
    private cdr: ChangeDetectorRef,
    private dataStore: DataStoreService,
    private router: Router,
    private popupService: PopupService,
    private activatedRoute: ActivatedRoute,
    private goToTop: GoToTopComponent,
    private routingInfoService: RoutingInfoService,
    private renderer: Renderer2,
    private authService: AuthService,
    private toastService: ToastService,
    private ccService: NgcCookieConsentService,
    private updates: SwUpdate,
    private appRef: ApplicationRef,
    private runTimeConfigService: RunTimeConfigService,
    private oauthService: OAuthService,
    private featureService: FeatureService,
    private communicationService: CommunicationService,
    private langService: LangService,
    private nonceService: NonceService
  ) {
    // this.isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;
    // this.isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

    this.authConfig = this.runTimeConfigService.getConfig('authConfig');

    this.oauthService.stopAutomaticRefresh();

    if (
      document.documentMode ||
      /Edge/.test(navigator.userAgent) ||
      /Edg/.test(navigator.userAgent) ||
      /Trident/.test(navigator.userAgent)
    ) {
      this.isIE = true;
    }

    this.setupTranslations();
  }

  ngOnInit() {
    this.nonce = this.nonceService.generateNonce(32);
    this.updateCSPNonce();
    this.addNonceToScripts();
    // this.addNonceToStyles();
    this.checkForCIAPAppUpdates();

    this.communicationService.message$.subscribe(message => {
      if (message && message.chatBot && message.chatBot === 'open') {
        this.showChatBot();
      }
    });

    this.isLoading.subscribe(_state => {
      this.isLoaderShowing = _state;
      this.cdr.detectChanges();
    });

    this.toastService.toasts.subscribe(data => {
      // detect the change manually using
      this.cdr.detectChanges();
    });

    this.router.events
      .pipe(
        filter((e: any) => e instanceof RoutesRecognized),
        pairwise()
      )
      .subscribe((e: any) => {
        this.routingInfoService.setValue(e[0].urlAfterRedirects);
      });

    this.router.events.forEach(event => {
      if (event instanceof NavigationStart) {
        this.toastService.clearAllToasts();
        this.loaderService.show();
      }

      if (event instanceof NavigationEnd) {
        this.loaderService.hide();
        this.goToTop.gotoTop();
      }

      // Set loading state to false in both of the below events to hide the spinner in case a request fails
      if (event instanceof NavigationCancel) {
        this.loaderService.hide();
      }
      if (event instanceof NavigationError) {
        this.loaderService.hide();
      }
    });

    this.router.events.pipe(filter(event => event instanceof ActivationStart)).subscribe(event => {
      this.popupService.dismissAllPopups();
      const routeData = event['snapshot'].data;
      this.noWrapperClass = routeData.noWrapperClass;
      if (routeData.class) {
        this.renderer.removeAttribute(document.body, 'class');
        this.renderer.addClass(document.body, routeData.class);
        this.themingService.applyCurrentTheme();
      } else {
        this.renderer.removeAttribute(document.body, 'class');
        this.themingService.applyCurrentTheme();
      }
      if (!this.authService.getUserProfileStatic()) {
        this.isAuthenticated = false;
      } else {
        this.isAuthenticated = true;
      }
    });

    if (this.authConfig.authMechanism === 'oauth2-oidc') {
      this.handleOAuthOIDCEvents();
    }

    // subscribe to cookieconsent observables to react to main events
    this.popupOpenSubscription = this.ccService.popupOpen$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.popupCloseSubscription = this.ccService.popupClose$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.initializeSubscription = this.ccService.initialize$.subscribe((event: NgcInitializeEvent) => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.statusChangeSubscription = this.ccService.statusChange$.subscribe((event: NgcStatusChangeEvent) => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.revokeChoiceSubscription = this.ccService.revokeChoice$.subscribe(() => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    this.noCookieLawSubscription = this.ccService.noCookieLaw$.subscribe((event: NgcNoCookieLawEvent) => {
      // you can use this.ccService.getConfig() to do stuff...
    });

    // this code is for opening chatbot automatically if turned on in user preferences
    // setTimeout(() => {
    //   this.showChatBot();
    // }, 20000);
  }

  ngOnDestroy() {
    // unsubscribe to cookieconsent observables to prevent memory leaks
    this.popupOpenSubscription.unsubscribe();
    this.popupCloseSubscription.unsubscribe();
    this.initializeSubscription.unsubscribe();
    this.statusChangeSubscription.unsubscribe();
    this.revokeChoiceSubscription.unsubscribe();
    this.noCookieLawSubscription.unsubscribe();
  }

  addNonceToScripts() {
    const scripts = document.querySelectorAll('script');
    scripts.forEach(script => {
      // If the script tag doesn't already have a nonce attribute, add it
      if (!script.hasAttribute('nonce')) {
        script.setAttribute('nonce', this.nonce);
      }
    });
  }

  addNonceToStyles() {
    const styleTags = document.querySelectorAll('style');
    styleTags.forEach((styleTag: HTMLStyleElement) => {
      styleTag.setAttribute('nonce', this.nonce);
    });
  }

  updateCSPNonce() {
    const metaCSP = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    const metaContentValue = cspConfig.cspContent;
    if (metaCSP) {
      metaCSP.setAttribute('content', metaContentValue);
    } else {
      // If no CSP meta tag exists, create and append one
      const newMeta = document.createElement('meta');
      newMeta.setAttribute('http-equiv', 'Content-Security-Policy');
      newMeta.setAttribute('content', metaContentValue);
      document.head.appendChild(newMeta);
    }
  }

  setupTranslations() {
    const isFeatureAvailable = this.featureService.isFeatureEnabled({
      feature: 'userProfile.languageTranslations'
    });
    this.translate.currentLang = '';
    if (isFeatureAvailable) {
      // this language will be used as a fallback when a translation isn't found in the current language
      this.translate.setDefaultLang(this.translate.getBrowserLang() || this.defaultLanguage);

      // the lang to use, if the lang isn't available, it will use the current loader to get them
      this.translate.use(this.translate.getBrowserLang() || this.defaultLanguage);
    } else {
      // default to en language
      this.translate.setDefaultLang(this.defaultLanguage);
      this.translate.use(this.defaultLanguage);
    }

    // this.translate.onLangChange.subscribe((e)=> {
    //   // e.lang
    // })
  }

  checkForCIAPAppUpdates() {
    if (!this.updates.isEnabled) {
      console.info('Service worker is not enabled');
      return;
    }
    console.info('Service worker is registered');

    this.updates.available.subscribe(event => {
      console.log('current version is', event.current);
      console.log('available version is', event.available);

      this.appUpdateAvailable = true; // update is available
    });
    this.updates.activated.subscribe(event => {
      console.log('old version was', event.previous);
      console.log('new version is', event.current);
    });

    let envInterval;
    if (environment.staging) {
      envInterval = 1 * 60 * 60 * 1000; // 1 hour
    } else if (environment.production) {
      envInterval = 120 * 60 * 60 * 1000; // 120 hours
    }

    // Allow the app to stabilize first, before starting polling for updates with `interval()`.
    const appIsStable$ = this.appRef.isStable.pipe(first(isStable => isStable === true));
    const atInterval$ = interval(envInterval);

    const everyIntervalOnceAppIsStable$ = concat(appIsStable$, atInterval$);

    everyIntervalOnceAppIsStable$.subscribe(() => {
      console.log(new Date() + ': Checking application updates...');
      this.updates
        .checkForUpdate()
        .then(() => {
          console.log(new Date() + ': Update check done');
        })
        .catch(err => {
          console.error(new Date() + ': Update check failed perhaps due to network error');
          console.log(err);
        });
    });
  }

  handleOAuthOIDCEvents() {
    this.oauthService.events.pipe(filter(e => e.type === 'session_terminated')).subscribe(e => {
      this.authService.destroySession();
      this.toastService.clearAllToasts();
      const navigationExtras: NavigationExtras = {
        state: {
          ADTokenError: true
        }
      };

      this.router.navigate(['auth/login-ad'], navigationExtras);
    });

    this.oauthService.events.subscribe(event => {
      //   | 'discovery_document_loaded'
      //   | 'jwks_load_error'
      //   | 'invalid_nonce_in_state'
      //   | 'discovery_document_load_error'
      //   | 'discovery_document_validation_error'
      //   | 'user_profile_loaded'
      //   | 'user_profile_load_error'
      //   | 'token_received'
      //   | 'token_error'
      //   | 'code_error'
      //   | 'token_refreshed'
      //   | 'token_refresh_error'
      //   | 'silent_refresh_error'
      //   | 'silently_refreshed'
      //   | 'silent_refresh_timeout'
      //   | 'token_validation_error'
      //   | 'token_expires'
      //   | 'session_changed'
      //   | 'session_error'
      //   | 'session_terminated'
      //   | 'logout'
      //   | 'popup_closed'
      //   | 'popup_blocked'
      //   | 'token_revoke_error';

      if (event instanceof OAuthErrorEvent) {
        console.error('OAuthErrorEvent Object:', event);
        if (event && event.type && event.type === 'discovery_document_load_error') {
          const navigationExtras: NavigationExtras = {
            state: {
              discoveryDocumentLoadError: true
            }
          };
          this.router.navigate(['error'], navigationExtras).then(r => {});
        } else if (event && event.type && event.type === 'token_refresh_error') {
          this.authService.destroySession();
          if (event.reason && event.reason['status'] && event.reason['status'] === 401) {
            const navigationExtras = {
              state: {
                unauthorizedError: true
              }
            };
            this.router.navigate(['error'], navigationExtras);
          } else {
            // this.oauthService.initLoginFlow();
            const navigationExtras = {
              state: {
                accessError: true,
                reason: event.type
              }
            };
            this.router.navigate(['error'], navigationExtras);
          }
        } else if (event && event.type && event.type === 'user_profile_load_error') {
          console.log('Cannot fetch user profile due to an error');
          const navigationExtras: NavigationExtras = {
            state: {
              profileFetchError: true
            }
          };
          this.router.navigate(['error'], navigationExtras);
        }
      } else if (event && event.type && event.type === 'session_changed') {
        this.oauthService.checkSession();
      } else if (event && event.type && event.type === 'token_received') {
        const idTokenExpiry = this.oauthService.getIdTokenExpiration();
        this.authService.setSessionExpiry(idTokenExpiry);
      }
      // else {
      //   console.warn('OAuthEvent Object:', event);
      // }
    });
  }

  showChatBot() {
    this.isbotVisible = true;
  }

  toogleChatBot(data: boolean) {
    if (!data) {
      this.isbotVisible = false;
    }
  }

  refreshChatBot(ev: boolean) {
    if (ev) {
      this.isbotVisible = false;
      setTimeout(() => {
        this.isbotVisible = true;
      });
    }
  }

  closeUpdateNotification() {
    this.appUpdateAvailable = false;
  }

  activateUpdate() {
    this.updates.activateUpdate().then(() => {
      location.reload();
    });
  }
}
